/**
 * The notetaker: turns a call recording into a transcript and a summary, and lists the
 * recordings a meeting tool (Zoom, Google Meet, a phone line) has made.
 *
 * MOCKED. Nothing leaves the browser: the transcript is canned text built from the call's
 * title. The real one (e.g. Deepgram) needs its API key kept server-side, so it would sit
 * behind zInspector or a small relay; only `createNotetaker` changes, the screens use the
 * `Notetaker` interface.
 */
import type { ACTIVITY_SOURCES } from "../providers/zinspector/crmConfig";

export type NotetakerSource = Extract<
  (typeof ACTIVITY_SOURCES)[number],
  "Zoom" | "Google Meet" | "Phone"
>;

export const NOTETAKER_SOURCES: NotetakerSource[] = [
  "Zoom",
  "Google Meet",
  "Phone",
];

export interface Transcript {
  summary: string;
  transcript: string;
}

export interface Recording extends Transcript {
  id: string;
  source: NotetakerSource;
  title: string;
  started_at: string;
  minutes: number;
}

export interface Notetaker {
  /** Whether this notetaker calls a real service (the UI says so when it doesn't). */
  isMock: boolean;
  transcribe: (audio: Blob, context: { title: string }) => Promise<Transcript>;
  /** Recent recordings from a meeting tool, newest first. */
  listRecordings: (
    source: NotetakerSource,
    context: { accountName: string },
  ) => Promise<Recording[]>;
}

const MOCK_DELAY_MS = 600;
const HOUR_MS = 60 * 60 * 1000;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const cannedTranscript = (title: string, who = "Client"): Transcript => ({
  summary: [
    `${title}: reviewed this month's inspection volume and turnaround.`,
    "Client asked about adding periodic inspections for their single-family portfolio.",
    "Next step: send the updated pricing and book a follow-up in two weeks.",
  ].join("\n"),
  transcript: [
    "[00:00] OSP: Thanks for making the time. How have the last inspections gone?",
    `[00:08] ${who}: Good overall. Reports are arriving within a day, which the owners like.`,
    "[00:21] OSP: Great. You mentioned periodic inspections last time.",
    `[00:27] ${who}: Yes, we'd like to cover the single-family homes twice a year.`,
    "[00:40] OSP: I'll send updated pricing and we can go over it in two weeks.",
  ].join("\n"),
});

export const mockNotetaker: Notetaker = {
  isMock: true,
  async transcribe(_audio, { title }) {
    await wait(MOCK_DELAY_MS);
    return cannedTranscript(title || "Call");
  },
  async listRecordings(source, { accountName }) {
    await wait(MOCK_DELAY_MS);
    const now = Date.now();
    return [1, 8].map((daysAgo, index) => {
      const title =
        index === 0
          ? `Weekly sync with ${accountName}`
          : `Pricing review with ${accountName}`;
      return {
        id: `${source}-${daysAgo}`,
        source,
        title,
        started_at: new Date(now - daysAgo * 24 * HOUR_MS).toISOString(),
        minutes: index === 0 ? 25 : 40,
        ...cannedTranscript(title),
      };
    });
  },
};

/** The notetaker the app uses. Swap for a real one here (Deepgram behind a relay). */
export const createNotetaker = (): Notetaker => mockNotetaker;
